import type { UseZoomableReturnType } from "./zoomable";
import { useMouseZoom } from "./mouse_zoom";
import { type Ref, ref, onMounted, onUnmounted } from "vue";
import type Konva from "konva";
import { useEditorStore } from "@/stores/editor_store";

interface UseMouseEventsArgs {
  zoomable: UseZoomableReturnType;
  stage: Ref<Konva.Stage>;
}

/**
 * useMouseEvents is a composable that provides all the mouse event handlers for the editor
 */
export function useMouseEvents(args: UseMouseEventsArgs): UseMouseEventsReturnType {
  const { zoomable, stage } = args;
  const store = useEditorStore();

  const { handleClickToZoom } = useMouseZoom(zoomable, stage);
  const isMetaKeyPressed = ref(false);

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.metaKey) {
      isMetaKeyPressed.value = true;
    }
  };

  const handleKeyUp = () => {
    isMetaKeyPressed.value = false;
  };

  const handleWindowBlur = () => {
    isMetaKeyPressed.value = false;
  };

  onMounted(() => {
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", handleWindowBlur);
  });

  onUnmounted(() => {
    document.removeEventListener("keydown", handleKeyDown);
    document.removeEventListener("keyup", handleKeyUp);
    window.removeEventListener("blur", handleWindowBlur);
  });

  const handleClickOnCanvas = (e: Konva.KonvaEventObject<MouseEvent>) => {
    // This is because when meta key is pressed, we have the panning functionality
    // and we don't want to trigger the zoom functionality when meta key is pressed
    // allowing the users to pan the image without zooming in or out.
    if (isMetaKeyPressed.value) {
      return;
    }

    if (store.openedPanel !== "eraseRestore" && e.evt.button === 0) {
      handleClickToZoom();
    }
  };

  return {
    handleClickOnCanvas,
  };
}

export interface UseMouseEventsReturnType {
  handleClickOnCanvas: (e: Konva.KonvaEventObject<MouseEvent>) => void;
}
